import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';

export interface ScrollPropertiesType {
	containerHeight: number;
	scrollTop: number;
}
export interface VirtualScrollProps {
	itemCount: number;
	itemRenderer: (index: number) => React.ReactNode;
	itemHeight: number;
	bufferHeight?: number;
	className?: string;
}
export interface VirtualScrollRef {
	scrollTo: (x: number, y: number) => void;
}

const VirtualScroll = forwardRef<VirtualScrollRef, VirtualScrollProps>((props: VirtualScrollProps, ref) => {
	const { itemCount, itemRenderer, className = '', itemHeight, bufferHeight: buffer } = props;
	const bufferHeight = buffer || itemHeight * 2;
	const [scrollProperties, setScrollProperties] = useState<ScrollPropertiesType>({
		containerHeight: 0,
		scrollTop: 0,
	});
	const scrollContainerRef = useRef<HTMLDivElement | null>(null);
	useImperativeHandle(ref, () => {
		return {
			scrollTo: (x: number, y: number) => {
				scrollContainerRef.current?.scrollTo(x, y);
			},
		};
	}, []);

	const handleScroll = useCallback(() => {
		if (scrollContainerRef.current) {
			const containerHeight = scrollContainerRef.current.offsetHeight;
			const scrollTop = scrollContainerRef.current.scrollTop;

			setScrollProperties({
				containerHeight,
				scrollTop,
			});
		}
	}, [scrollContainerRef]);

	useEffect(() => {
		handleScroll();
	}, [handleScroll]);

	useEffect(() => {
		if (scrollContainerRef.current) {
			const resizeObserver = new ResizeObserver(handleScroll);
			resizeObserver.observe(scrollContainerRef.current);
			return () => {
				resizeObserver.disconnect();
			};
		}
	}, [scrollContainerRef, handleScroll]);

	const { containerHeight, scrollTop } = scrollProperties;

	const totalHeight = itemCount * itemHeight;
	const topBound = Math.max(0, Math.floor((scrollTop - bufferHeight) / itemHeight));
	const topBoundHeight = topBound * itemHeight;

	const itemsToRender = Math.min(Math.ceil((containerHeight + bufferHeight * 2) / itemHeight), itemCount - topBound);
	const bottomBoundHeight = totalHeight - (topBoundHeight + itemsToRender * itemHeight);

	const renderedItems: React.ReactNode[] = [];
	for (let i = topBound; i < topBound + itemsToRender; i++) {
		renderedItems.push(itemRenderer(i));
	}

	return (
		<div
			className={`virtual-scroll-container ${className ? className : ''}`}
			ref={scrollContainerRef}
			onScroll={handleScroll}
		>
			<div style={{ minHeight: topBoundHeight, maxHeight: topBoundHeight }} />
			{renderedItems}
			<div style={{ minHeight: bottomBoundHeight, maxHeight: bottomBoundHeight }} />
		</div>
	);
});
VirtualScroll.displayName = 'VirtualScroll';

export default VirtualScroll;
